/**
 * Remembers the route a user was trying to reach when they got bounced to
 * /login, so we can send them back there after a successful (re)login instead
 * of always dumping them on a hardcoded landing page.
 *
 * Uses sessionStorage so the target survives the full-page reload the axios
 * request interceptor triggers (window.location.href = '/login') and the
 * multi-step login flow (/login -> /login/email -> /login/password), where
 * react-router location state would be lost.
 */
const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

// Routes we must never send the user "back" to after logging in — auth/entry
// pages would create a redirect loop or land them nowhere useful.
const isBlockedTarget = (path?: string): boolean => {
    if (!path) return true;

    const pathname = path.split('?')[0].toLowerCase();

    return (
        pathname === '/' ||
        pathname.startsWith('/login') ||
        pathname.startsWith('/register') ||
        pathname.startsWith('/reset-password') ||
        pathname.startsWith('/create-password') ||
        pathname.startsWith('/update-profile') ||
        pathname.startsWith('/maintenance')
    );
};

export const setPostLoginRedirect = (path?: string): void => {
    try {
        if (isBlockedTarget(path)) return;

        sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path as string);
    } catch (error) {
        console.warn('Unable to store post-login redirect', error);
    }
};

export const consumePostLoginRedirect = (fallback: string): string => {
    try {
        const target = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);

        sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);

        if (target && !isBlockedTarget(target)) return target;
    } catch (error) {
        console.warn('Unable to read post-login redirect', error);
    }

    return fallback;
};
